import { createRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { meLayoutRoute } from './meLayout.js';
import type { CurrencyCode } from '@kora/types';
import { cancelMyRequest, confirmMyAdvanceReceipt, listMyAdvances, type MyAdvance } from '../api/advances.js';
import { formatDate, formatMoney } from '../lib/format.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

/**
 * /me/loans — the employee's own advances (ADR-0009 D11). For `disbursed`
 * advances a "Confirm I received it" button arms payroll deductions; `active`
 * advances show the running balance + paid-to-date + full ledger.
 */
export const myLoansRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/loans',
  component: MyLoansPage,
});

function MyLoansPage(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['me-advances'],
    queryFn: listMyAdvances,
  });
  const confirm = useMutation({
    mutationFn: (id: string) => confirmMyAdvanceReceipt(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me-advances'] }),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => cancelMyRequest(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me-advances'] }),
  });

  return (
    <section className="p-8 space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl">My loans</h1>
          <p className="text-sm text-gray-400">Advances and salary loans, with deductions reconciled against your payslip.</p>
        </div>
        <Link to="/me/loans/request" className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 whitespace-nowrap">
          Request a loan
        </Link>
      </header>

      {isLoading ? <p className="text-gray-500">Loading...</p> : null}
      {isError ? (
        <Alert variant="error">
          {error instanceof ApiError ? error.message : 'Could not load your loans.'}
        </Alert>
      ) : null}
      {data && data.advances.length === 0 ? (
        <div className="rounded border border-dashed border-gray-800 p-10 text-center text-gray-400">
          You have no loans yet. Use “Request a loan” to apply.
        </div>
      ) : null}

      <div className="space-y-4">
        {data?.advances.map((a) => (
          <LoanCard
            key={a.id}
            loan={a}
            onConfirm={() => confirm.mutate(a.id)}
            confirming={confirm.isPending}
            onCancel={() => cancel.mutate(a.id)}
            cancelling={cancel.isPending}
          />
        ))}
      </div>
    </section>
  );
}

function LoanCard({
  loan,
  onConfirm,
  confirming,
  onCancel,
  cancelling,
}: {
  loan: MyAdvance;
  onConfirm: () => void;
  confirming: boolean;
  onCancel: () => void;
  cancelling: boolean;
}): JSX.Element {
  const [showLedger, setShowLedger] = useState(false);
  const ccy = loan.currency as CurrencyCode;
  const paidToDate = (Number(loan.principalAmount) - Number(loan.remainingBalance)).toFixed(4);
  return (
    <article className="rounded border border-gray-900 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-medium text-white capitalize">{loan.kind.replace('_', ' ')}</h2>
          <p className="text-xs text-gray-500">Started {formatDate(loan.startPeriod)}</p>
        </div>
        <span className="text-xs rounded px-2 py-1 border border-gray-700 text-gray-300">{loan.status}</span>
      </div>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <KV k="Principal" v={formatMoney(loan.principalAmount, ccy)} />
        <KV k="Monthly deduction" v={formatMoney(loan.monthlyDeduction, ccy)} />
        <KV k="Paid to date" v={formatMoney(paidToDate, ccy)} />
        <KV k="Remaining" v={formatMoney(loan.remainingBalance, ccy)} bold />
      </dl>

      {loan.requestPurpose ? (
        <p className="text-xs text-gray-500">Purpose: <span className="text-gray-300">{loan.requestPurpose}</span></p>
      ) : null}

      {loan.status === 'rejected' && loan.rejectedReason ? (
        <p className="text-xs text-red-400">Declined: {loan.rejectedReason}</p>
      ) : null}

      {loan.status === 'disbursed' ? (
        <button
          type="button"
          onClick={onConfirm}
          disabled={confirming}
          className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
        >
          {confirming ? 'Confirming…' : 'Confirm I received it'}
        </button>
      ) : null}

      {loan.status === 'requested' ? (
        <button
          type="button"
          onClick={onCancel}
          disabled={cancelling}
          className="rounded border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-900 disabled:opacity-50"
        >
          {cancelling ? 'Cancelling…' : 'Cancel request'}
        </button>
      ) : null}

      {loan.ledger.length > 0 ? (
        <div>
          <button type="button" onClick={() => setShowLedger((s) => !s)} className="text-xs text-gray-400 hover:underline">
            {showLedger ? 'Hide' : 'Show'} ledger ({loan.ledger.length})
          </button>
          {showLedger ? (
            <table className="w-full text-xs mt-2">
              <thead className="text-gray-600 uppercase text-[0.65rem]">
                <tr><th className="text-left py-1">Date</th><th className="text-left py-1">Type</th><th className="text-right py-1">Amount</th></tr>
              </thead>
              <tbody>
                {loan.ledger.map((e, i) => (
                  <tr key={i} className="border-t border-gray-900">
                    <td className="py-1 text-gray-400">{formatDate(e.effectiveDate)}</td>
                    <td className="py-1 text-gray-300">{e.kind}</td>
                    <td className="py-1 text-right">{formatMoney(e.amount, ccy)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function KV({ k, v, bold }: { k: string; v: string; bold?: boolean }): JSX.Element {
  return (
    <div>
      <dt className="text-gray-500 text-xs">{k}</dt>
      <dd className={`font-mono text-xs ${bold ? 'text-white' : 'text-gray-300'}`}>{v}</dd>
    </div>
  );
}
